import { ParentSize } from '@visx/responsive'
import { AnimatedAxis, AnimatedGrid, AnimatedLineSeries, Tooltip, XYChart } from '@visx/xychart'
import { format, parse } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TimeScale } from 'src/lib/contexts/AnalyticsContext/types'

interface DataPoint {
  x: string
  y: number
}

export interface LineSeries {
  isPercentage?: boolean
  selectedTimeScale: TimeScale
  series: {
    label: string
    color: string
    data: DataPoint[]
  }[]
}

interface LineChartProps {
  width: number
  height: number
  isPercentage?: boolean
  selectedTimeScale: TimeScale
  series: {
    color: string
    label: string
    data: DataPoint[]
  }[]
}

const possibleFormats = [
  'MMM dd yyyy hh:mm a', // e.g. May 07 2025 06:00 PM
  'MMM dd yyyy', // e.g. May 07 2025
  "yyyy-MM-dd'T'HH:mm:ssX", // ISO with timezone
  "yyyy-MM-dd'T'HH:mm:ss.SSSX", // ISO with ms
  'yyyy-MM-dd', // e.g. 2025-05-07
  'MMM dd' // e.g. May 07
]

const parseDate = (dateStr: string) => {
  for (const formatString of possibleFormats) {
    const parsed = parse(dateStr, formatString, new Date())
    if (!isNaN(parsed.getTime())) {
      return parsed
    }
  }
  // Fallback to Date constructor if none match
  return new Date(dateStr)
}

const accessors = {
  xAccessor: (d: DataPoint) => d?.x && parseDate(d.x),
  yAccessor: (d: DataPoint) => d?.y
}

export const LineChartComponent = ({ width, height, series, isPercentage, selectedTimeScale }: LineChartProps) => {
  const legendRef = useRef<HTMLDivElement>(null)
  // Calculate domains
  const allDates = series.flatMap(s => s.data.map(d => parseDate(d.x)))
  const allValues = series.flatMap(s => s.data.map(d => d.y))
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())))
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())))
  const maxY = Math.max(...allValues)

  const [xychartHeight, setXychartHeight] = useState(height)

  useEffect(() => {
    function updateXychartHeight() {
      if (legendRef.current) {
        const legendHeight = legendRef.current.offsetHeight
        const chartHeight = height
        setXychartHeight(chartHeight - legendHeight + 20)
      }
    }
    updateXychartHeight()

    window.addEventListener('resize', updateXychartHeight)
    return () => window.removeEventListener('resize', updateXychartHeight)
  }, [width, height])

  return (
    <div>
      <XYChart
        width={width}
        height={xychartHeight}
        xScale={{ type: 'time', domain: [minDate, maxDate] }}
        yScale={{ type: 'linear', domain: [0, maxY * 1.1] }}
        margin={{ top: 20, right: 20, bottom: 60, left: 40 }}
      >
        <AnimatedAxis
          orientation='bottom'
          tickFormat={value => format(parseDate(value), selectedTimeScale === 'daily' ? 'MMM dd' : 'hh:mm a')}
          strokeWidth={0.5}
          stroke='#F0F0F0'
          hideAxisLine
          tickLength={8}
          numTicks={Math.floor(width / 100)}
          labelProps={{
            fill: '#6E6E6E',
            fontSize: 12,
            textAnchor: 'middle',
            transform: 'rotate(-45)',
            dy: '0.5em',
            dx: '-0.5em'
          }}
        />
        <AnimatedAxis
          orientation='left'
          numTicks={5}
          hideAxisLine
          strokeWidth={0.5}
          stroke='#F0F0F0'
          tickLength={0}
          labelOffset={45}
          tickFormat={value => (isPercentage ? `${value}%` : value.toLocaleString())}
          labelProps={{
            fill: '#6E6E6E',
            fontSize: 12
          }}
        />
        <AnimatedGrid columns={false} numTicks={5} strokeWidth={0.5} lineStyle={{ stroke: '#F0F0F0' }} />
        {series.map(({ color, data, label }) => (
          <AnimatedLineSeries key={label} dataKey={label} data={data} stroke={color} strokeWidth={2} {...accessors} />
        ))}
        <Tooltip
          showVerticalCrosshair
          snapTooltipToDatumX
          snapTooltipToDatumY
          showSeriesGlyphs
          renderGlyph={({ key, color }) => {
            const seriesColor = series.find(s => s.label === key)?.color
            return <circle r={4} fill={seriesColor} stroke='white' strokeWidth={2} />
          }}
          style={{
            position: 'absolute',
            zIndex: 1000,
            boxShadow: '0px 0px 12px 0px #0000001A',
            pointerEvents: 'none'
          }}
          className='bg-white absolute z-1000 rounded-lg p-2 shadow-md w-fit'
          renderTooltip={({ tooltipData, colorScale }) => {
            if (!tooltipData?.nearestDatum || !colorScale) return null

            const date = parseDate((tooltipData.nearestDatum.datum as DataPoint).x)

            return (
              <div className='bg-white p-2 rounded-md whitespace-nowrap'>
                <ul>
                  {Object.entries(tooltipData.datumByKey).map(([key, value]) => {
                    if (!value?.datum) return null
                    const datum = value.datum as DataPoint
                    return (
                      <li key={key} className='text-sm font-medium text-coal-dark flex items-center'>
                        <div
                          className='text-coal-light text-xs font-medium h-1 w-2 mr-1 inline-block rounded-full'
                          style={{ backgroundColor: series.find(s => s.label === key)?.color }}
                        />
                        <span className='text-coal-light text-xs font-medium mr-1'>{key}:</span>
                        <span className='text-coal-dark text-xs font-bold'>
                          {isPercentage ? `${datum.y}%` : datum.y.toLocaleString()}
                        </span>
                      </li>
                    )
                  })}
                </ul>
                <div className='text-coal-dark text-xs font-bold mt-2'>
                  {selectedTimeScale === 'daily'
                    ? format(date, 'MMM dd')
                    : format(date, selectedTimeScale === 'hourly' ? 'hh:mm a' : 'MMM dd hh:mm a')}
                </div>
              </div>
            )
          }}
        />
      </XYChart>
      <div className='absolute bottom-0 left-0 flex justify-center items-center gap-2 w-full flex-wrap' ref={legendRef}>
        {series.map(({ color, label }) => (
          <div key={label} className='flex justify-center items-center'>
            <div className='w-4 h-1 mr-2' style={{ backgroundColor: color }} />
            <div className='text-xs text-nowrap'>{label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const LineChart = ({ series, isPercentage, selectedTimeScale }: LineSeries) => {
  return (
    <div className='w-full h-full flex items-center justify-center relative overflow-hidden'>
      <ParentSize className='w-full h-full' debounceTime={10}>
        {({ width, height }) => (
          <LineChartComponent
            width={width}
            height={height}
            series={series}
            isPercentage={isPercentage}
            selectedTimeScale={selectedTimeScale}
          />
        )}
      </ParentSize>
    </div>
  )
}
